import React, { useState } from 'react';
import { array, bool, object, string } from 'prop-types';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { parse } from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { getQueryParamNames } from '../../util/search';

import {
  EmptyState,
  ListingCard,
  Icons,
  InlineTextButton,
  LoadingPlaceholder,
  Notice,
  PaginationLinks,
} from '../../components';
import FilterComponent from '../SearchPage/FilterComponent';
import ShowOnlyFilters from '../SearchPage/ShowOnlyFilters/ShowOnlyFilters';
import {
  groupListingFieldConfigs,
  initialValues as getInitialValues,
  validFilterParams,
  cleanSearchFromConflictingParams,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
} from '../SearchPage/SearchPage.shared';

import css from './ListingsTabContent.module.css';

const TagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8Z" />
    <circle cx="8" cy="8" r="1.5" />
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const ListingsTabContent = props => {
  const {
    listings,
    queryListingsInProgress,
    queryListingsError,
    config,
    listingCategories,
    location,
    history,
    routeConfiguration,
    className,
    rootClassName,
    username,
    filterCounts,
    pagination,
  } = props;

  const intl = useIntl();

  // Get filter configurations
  const { listingFields: listingFieldsConfig } = config?.listing || {};
  const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
  const activeListingTypes = config?.listing?.listingTypes?.map(c => c.listingType) || [];
  const marketplaceCurrency = config.currency;

  // Parse current URL params
  const searchParams = parse(location.search);
  // The page is the paging's own: a changed filter starts from the first one
  const { tab, reviewType, keywords: urlKeywords, page, ...filterParamsFromUrl } = searchParams;

  // State for filter sidebar visibility
  const [isFiltersVisible, setIsFiltersVisible] = useState(true);

  // Valid filter params from URL
  const validQueryParams = validFilterParams(
    filterParamsFromUrl,
    listingFieldsConfig,
    defaultFiltersConfig,
    false
  );

  // Filter configuration similar to SearchPage
  const categoryFilters = defaultFiltersConfig?.filter(f => f.schemaType === 'category') || [];

  const priceFilters = defaultFiltersConfig?.filter(f => f.key === 'price') || [];
  const otherDefaultFilters =
    defaultFiltersConfig?.filter(
      f => f.schemaType !== 'category' && !['price', 'keywords', 'dates'].includes(f.key)
    ) || [];

  // Use category-limited filtering
  const categoryFilteredListingFields = pickListingFieldFilters({
    listingFields: listingFieldsConfig || [],
    locationSearch: location.search,
    categoryConfiguration: {
      key: 'categoryLevel',
      categories: listingCategories,
    },
  });

  const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
    categoryFilteredListingFields,
    activeListingTypes
  );

  // Combined filters for sidebar
  const availableFilters = [
    ...categoryFilters,
    ...customPrimaryFilters,
    ...customSecondaryFilters,
    ...priceFilters,
    ...otherDefaultFilters,
  ];

  // Check if any filter is active
  const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);
  const hasActiveFilters = filterQueryParamNames.some(name => validQueryParams[name] != null);

  // Handle filter changes
  const getHandleChangedValueFn = useHistoryPush => updatedURLParams => {
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    // The filters are the URL's: back and forward change them too
    const cleanedParams = omitLimitedListingFieldParams(
      {
        ...filterParamsFromUrl,
        ...updatedURLParams,
      },
      filterConfigs
    );

    if (useHistoryPush) {
      const searchParamsToUse = cleanSearchFromConflictingParams(
        cleanedParams,
        listingFieldsConfig,
        defaultFiltersConfig,
        sortConfig
      );

      // Keep tab param and keywords from URL
      const finalParams = {
        ...searchParamsToUse,
        tab: 'listings',
        ...(urlKeywords ? { keywords: urlKeywords } : {}),
      };

      history.push(
        createResourceLocatorString('ProfilePage', routeConfiguration, { username }, finalParams)
      );
    }
  };

  // Reset all filters — also clears showSoldOut since it's not a config-driven
  // filter param and won't be caught by the standard filter reset logic.
  const resetAll = () => {
    history.push(
      createResourceLocatorString(
        'ProfilePage',
        routeConfiguration,
        { username },
        { tab: 'listings' }
      )
    );
  };

  // Handler for the "Show Only > Sold Out Items" toggle.
  // showSoldOut is a UI-only URL param — see ProfilePage.duck.js queryUserListings()
  // for how it is consumed before the Sharetribe API call.
  const handleToggleSoldOut = checked => {
    const currentParams = omit(parse(location.search), 'page');
    const updatedParams = checked
      ? { ...currentParams, showSoldOut: 'true' }
      : omit(currentParams, 'showSoldOut');
    history.push(
      createResourceLocatorString('ProfilePage', routeConfiguration, { username }, updatedParams)
    );
  };

  // Create initialValues function for filters
  const initialValuesFn = getInitialValues({ location, config }, filterParamsFromUrl);

  const classes = classNames(rootClassName || css.root, className);
  const hasListings = listings.length > 0;
  // Filters, words or "sold out" narrowed the list: say so, and offer to clear them
  const isNarrowed = hasActiveFilters || !!urlKeywords || searchParams.showSoldOut === 'true';

  return (
    <div className={classes}>
      {/* Sidebar with filters */}
      {isFiltersVisible && (
        <aside className={css.sidebar}>
          <div className={css.filterSidebarHead}>
            <h2>
              <FormattedMessage id="ListingsTabContent.filterTitle" />
              <InlineTextButton
                className={css.closeFiltersButton}
                onClick={() => setIsFiltersVisible(false)}
              >
                <Icons name="cross" />
              </InlineTextButton>
            </h2>
          </div>
          <div className={css.filterColumnContent}>
            {availableFilters.map(filterConfig => (
              <FilterComponent
                key={`ProfileFilter.${filterConfig.scope || 'built-in'}.${filterConfig.key}`}
                idPrefix="ProfileFilter"
                className={css.filter}
                config={filterConfig}
                marketplaceCurrency={marketplaceCurrency}
                urlQueryParams={validQueryParams}
                initialValues={initialValuesFn}
                getHandleChangedValueFn={getHandleChangedValueFn}
                intl={intl}
                listingCategories={listingCategories}
                filterCounts={filterCounts}
                liveEdit
                showAsPopup={false}
                isDesktop
              />
            ))}

            {/* "Show Only" section — always rendered after the config-driven filters */}
            <ShowOnlyFilters
              urlQueryParams={parse(location.search)}
              onToggleSoldOut={handleToggleSoldOut}
              className={css.filter}
            />

            <button type="button" className={css.resetAllButton} onClick={resetAll}>
              <FormattedMessage id="ListingsTabContent.clearAllFilters" />
            </button>
          </div>
        </aside>
      )}

      {/* Main content */}
      <div className={classNames(css.mainContent, { [css.fullWidth]: !isFiltersVisible })}>
        {/* Show filters button when sidebar is hidden */}
        {!isFiltersVisible && (
          <button
            type="button"
            className={css.showFiltersButton}
            onClick={() => setIsFiltersVisible(true)}
          >
            <Icons name="filter" />
            <FormattedMessage id="ListingsTabContent.showFilters" />
          </button>
        )}
        {/* One state at a time: loading, error, nothing found, or the cards */}
        {queryListingsInProgress ? (
          <LoadingPlaceholder variant="cards" count={8} />
        ) : queryListingsError ? (
          <Notice tone="error">
            <p>
              <FormattedMessage id="ListingsTabContent.queryError" />
            </p>
          </Notice>
        ) : hasListings ? (
          <>
            <ul className={css.listingsGrid}>
              {listings.map(listing => (
                <li key={listing.id.uuid} className={css.listingItem}>
                  <ListingCard
                    className={css.listingCard}
                    listing={listing}
                    showAuthorInfo={false}
                  />
                </li>
              ))}
            </ul>
            {pagination?.totalPages > 1 ? (
              <PaginationLinks
                className={css.pagination}
                pageName="ProfilePage"
                pagePathParams={{ username }}
                pageSearchParams={omit(searchParams, 'page')}
                pagination={pagination}
              />
            ) : null}
          </>
        ) : isNarrowed ? (
          <EmptyState
            icon={<SearchIcon />}
            title={intl.formatMessage({ id: 'ListingsTabContent.noMatchesTitle' })}
            text={intl.formatMessage({ id: 'ListingsTabContent.noMatchesText' })}
            action={
              <button type="button" className={css.secondaryAction} onClick={resetAll}>
                <FormattedMessage id="ListingsTabContent.clearAllFilters" />
              </button>
            }
          />
        ) : (
          <EmptyState
            icon={<TagIcon />}
            title={intl.formatMessage({ id: 'ListingsTabContent.noListingsTitle' })}
            text={intl.formatMessage({ id: 'ListingsTabContent.noListingsText' })}
          />
        )}
      </div>
    </div>
  );
};

ListingsTabContent.defaultProps = {
  className: null,
  rootClassName: null,
  listings: [],
  queryListingsInProgress: false,
  queryListingsError: null,
  listingCategories: [],
  filterCounts: {},
  pagination: null,
};

ListingsTabContent.propTypes = {
  className: string,
  rootClassName: string,
  listings: array,
  queryListingsInProgress: bool,
  queryListingsError: propTypes.error,
  config: object.isRequired,
  listingCategories: array,
  location: object.isRequired,
  history: object.isRequired,
  routeConfiguration: array.isRequired,
  username: string.isRequired,
  filterCounts: object,
  pagination: propTypes.pagination,
};

export default ListingsTabContent;
